import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { documentsApi } from '@services/api';
import {
  formatMoney,
  moneyToCents,
  centsToMoney,
  nassauToday,
  VAT_SOFT_THRESHOLD,
  VAT_STRONG_THRESHOLD,
} from '@utils';
import { LoadingSpinner, EmptyState } from '@components/common';
import type { DocumentListItem } from '@types';

const card = 'rounded-2xl border border-hairline bg-white p-6 shadow-sm';
const sectionHead = 'text-[15px] font-semibold text-gray-900';
const caption = 'font-mono text-[11px] text-gray-400';

/** Page through the documents list — there is no server aggregation endpoint,
 * so every panel here is derived client-side (labelled as such). Capped to
 * avoid a runaway loop on a very large history. */
async function fetchAllDocuments(): Promise<DocumentListItem[]> {
  const all: DocumentListItem[] = [];
  for (let page = 1; page <= 20; page++) {
    const res = await documentsApi.list({ page });
    all.push(...res.items);
    if (!res.pagination.has_next) break;
  }
  return all;
}

function useRevenueModel() {
  const query = useQuery({
    queryKey: ['documents', 'revenue-all'],
    queryFn: fetchAllDocuments,
    staleTime: 60_000,
  });

  const model = useMemo(() => {
    const today = nassauToday();
    const [y, m] = today.split('-').map(Number);
    const months: { key: string; label: string; monthIdx: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(Date.UTC(y!, m! - 1 - i, 1));
      months.push({
        key: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }),
        monthIdx: d.getUTCMonth(),
      });
    }
    const windowKeys = new Set(months.map(mo => mo.key));
    const items = query.data ?? [];

    const monthly = new Map(months.map(mo => [mo.key, 0]));
    const byClient = new Map<string, number>();
    let invoiceCents = 0;
    let quoteCents = 0;

    for (const doc of items) {
      const cents = moneyToCents(doc.total) ?? 0;
      const inRevenue =
        doc.type === 'invoice' &&
        (doc.status === 'ISSUED' || doc.status === 'PAID');
      if (doc.type === 'invoice' && doc.status !== 'VOID')
        invoiceCents += cents;
      if (
        doc.type === 'quote' &&
        (doc.status === 'ISSUED' ||
          doc.status === 'ACCEPTED' ||
          doc.status === 'CONVERTED')
      )
        quoteCents += cents;
      if (inRevenue && doc.issue_date) {
        const key = doc.issue_date.slice(0, 7);
        if (windowKeys.has(key))
          monthly.set(key, (monthly.get(key) ?? 0) + cents);
        byClient.set(
          doc.client_name,
          (byClient.get(doc.client_name) ?? 0) + cents
        );
      }
    }

    const series = months.map(mo => ({
      ...mo,
      cents: monthly.get(mo.key) ?? 0,
    }));
    let running = 0;
    const cumulative = series.map(s => {
      running += s.cents;
      return { ...s, cumCents: running };
    });
    const totalCents = running;
    const maxBar = Math.max(1, ...series.map(s => s.cents));
    const topClients = [...byClient.entries()]
      .map(([name, cents]) => ({ name, cents }))
      .sort((a, b) => b.cents - a.cents)
      .slice(0, 5);
    const maxClient = Math.max(1, ...topClients.map(c => c.cents));
    const splitTotal = invoiceCents + quoteCents;
    const invoicePct = splitTotal ? (invoiceCents / splitTotal) * 100 : 0;

    return {
      months,
      series,
      cumulative,
      totalCents,
      maxBar,
      topClients,
      maxClient,
      invoiceCents,
      quoteCents,
      invoicePct,
      windowLabel: `${months[0]!.label} ${months[0]!.key.slice(0, 4)} – ${months[11]!.label} ${months[11]!.key.slice(0, 4)}`,
      hasData: items.length > 0,
    };
  }, [query.data]);

  return { ...query, model };
}

export default function Revenue() {
  const { isLoading, isError, refetch, model } = useRevenueModel();

  if (isLoading) {
    return (
      <div data-testid="revenue-loading" className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (isError) {
    return (
      <div data-testid="revenue-error">
        <EmptyState
          title="Couldn't load revenue"
          description="Something went wrong fetching the documents. Please try again."
          action={{ label: 'Retry', onClick: () => refetch() }}
        />
      </div>
    );
  }

  const barMax = 150; // px
  const ceilingCents = VAT_STRONG_THRESHOLD * 100;

  // Cumulative area geometry.
  const chW = 1100;
  const chH = 240;
  const cumPoints = model.cumulative.map((c, i) => {
    const x = 40 + (i / 11) * (chW - 80);
    const yv = chH - Math.min(c.cumCents / ceilingCents, 1) * (chH - 8);
    return { x, y: yv };
  });
  const cumLine = cumPoints.map(p => `${p.x},${p.y}`).join(' ');
  const cumArea = `M ${cumPoints.map(p => `${p.x} ${p.y}`).join(' L ')} L ${chW - 40} ${chH} L 40 ${chH} Z`;
  const softY = chH - (VAT_SOFT_THRESHOLD / VAT_STRONG_THRESHOLD) * (chH - 8);
  const lastCum = cumPoints[cumPoints.length - 1]!;

  return (
    <div data-testid="revenue-page" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-[26px] font-medium text-gray-900">
          Revenue — year at a glance
        </h1>
        <span className="inline-flex h-[38px] items-center rounded-[9px] border-[1.5px] border-primary-600 bg-primary-50 px-3.5 text-[13px] font-semibold text-primary-800">
          {model.windowLabel}
        </span>
      </div>

      {/* Caveat */}
      <div className="flex items-start gap-2.5 rounded-xl border border-[#dbe3ee] bg-white px-4 py-3">
        <InformationCircleIcon className="mt-px h-4 w-4 flex-shrink-0 text-gray-500" />
        <p className="text-[12.5px] text-gray-500">
          Every panel is{' '}
          <strong className="text-gray-700">derived client-side</strong> from
          the documents list — there is no server aggregation endpoint. A large
          history pages through{' '}
          <span className="font-mono">GET /documents</span>; a dedicated rollup
          endpoint is recommended for production.
        </p>
      </div>

      {!model.hasData ? (
        <div className={`${card} py-14 text-center`}>
          <h3 className="font-serif text-[19px] font-medium text-gray-900">
            No revenue to chart yet
          </h3>
          <p className="mt-1.5 text-[13px] text-gray-500">
            Issue your first invoice to start building the yearly view.
          </p>
        </div>
      ) : (
        <>
          {/* Row 1: monthly bars + invoice/quote split */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
            <div className={card}>
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <span className={sectionHead}>Monthly revenue</span>
                  <p className={`${caption} mt-0.5`}>
                    Rolled up by issue_date · trailing 12 months
                  </p>
                </div>
                <div className="font-mono text-[12px] text-gray-500">
                  Total{' '}
                  <span className="font-semibold text-gray-900">
                    {formatMoney(centsToMoney(model.totalCents))}
                  </span>
                </div>
              </div>
              <div
                className="flex items-end gap-3"
                style={{ height: barMax + 20 }}
              >
                {model.series.map((s, i) => {
                  const h = (s.cents / model.maxBar) * barMax;
                  const isCurrent = i === model.series.length - 1;
                  return (
                    <div
                      key={s.key}
                      className="flex flex-1 flex-col items-center justify-end"
                      style={{ height: '100%' }}
                      title={`${s.label}: ${formatMoney(centsToMoney(s.cents))}`}
                    >
                      <div
                        className={`w-full max-w-[34px] rounded-t-md ${isCurrent ? 'bg-amber-500' : 'bg-primary-300'}`}
                        style={{ height: Math.max(2, h) }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex gap-3">
                {model.series.map((s, i) => (
                  <span
                    key={s.key}
                    className={`flex-1 text-center font-mono text-[10px] ${
                      i === model.series.length - 1
                        ? 'font-semibold text-amber-700'
                        : 'text-gray-400'
                    }`}
                  >
                    {s.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Invoice vs quote split */}
            <div className={card}>
              <span className={sectionHead}>Invoice vs quote</span>
              <p className={`${caption} mt-0.5`}>By summed total</p>
              <div className="my-5 flex justify-center">
                <div
                  className="flex h-[150px] w-[150px] items-center justify-center rounded-full"
                  style={{
                    background: `conic-gradient(#2563eb 0 ${model.invoicePct}%, #14b8a6 ${model.invoicePct}% 100%)`,
                  }}
                >
                  <div className="flex h-[92px] w-[92px] flex-col items-center justify-center rounded-full bg-white">
                    <span className="font-mono text-[18px] font-semibold text-gray-900">
                      {formatMoney(
                        centsToMoney(model.invoiceCents + model.quoteCents)
                      )}
                    </span>
                    <span className="text-[10px] text-gray-400">total</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2.5">
                <SplitRow
                  color="#2563eb"
                  label="Invoices"
                  cents={model.invoiceCents}
                  pct={model.invoicePct}
                />
                <SplitRow
                  color="#14b8a6"
                  label="Quotes"
                  cents={model.quoteCents}
                  pct={100 - model.invoicePct}
                />
              </div>
            </div>
          </div>

          {/* Row 2: cumulative vs VAT + top clients */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
            <div className={card}>
              <div className="mb-4">
                <span className={sectionHead}>
                  Cumulative revenue vs VAT ceiling
                </span>
                <p className={`${caption} mt-0.5`}>
                  Running total toward the $100,000 BSD threshold
                </p>
              </div>
              <div className="relative">
                <svg
                  width="100%"
                  height={chH}
                  viewBox={`0 0 ${chW} ${chH}`}
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient id="cum" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#2563eb" stopOpacity="0.16" />
                      <stop offset="1" stopColor="#2563eb" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <line
                    x1="0"
                    y1="4"
                    x2={chW}
                    y2="4"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeDasharray="6 5"
                  />
                  <line
                    x1="0"
                    y1={softY}
                    x2={chW}
                    y2={softY}
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    strokeDasharray="6 5"
                  />
                  <path d={cumArea} fill="url(#cum)" />
                  <polyline
                    points={cumLine}
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  <circle cx={lastCum.x} cy={lastCum.y} r="5" fill="#2563eb" />
                </svg>
                <span className="absolute right-1.5 top-0 bg-white px-1 font-mono text-[10px] font-semibold text-red-600">
                  $100k ceiling
                </span>
                <span
                  className="absolute right-1.5 bg-white px-1 font-mono text-[10px] font-semibold text-amber-700"
                  style={{ top: softY - 6 }}
                >
                  $80k soft
                </span>
              </div>
              <div className="mt-2 flex gap-3">
                {model.series.map(s => (
                  <span
                    key={s.key}
                    className="flex-1 text-center font-mono text-[10px] text-gray-400"
                  >
                    {s.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Top clients */}
            <div className={card}>
              <span className={sectionHead}>Top clients this year</span>
              <p className={`${caption} mt-0.5`}>By summed total</p>
              <div className="mt-4 flex flex-col gap-4">
                {model.topClients.map((c, i) => (
                  <div key={c.name}>
                    <div className="mb-1.5 flex justify-between">
                      <span className="text-[13px] font-medium text-gray-700">
                        {c.name}
                      </span>
                      <span className="font-mono text-[12.5px] text-gray-900">
                        {formatMoney(centsToMoney(c.cents))}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-[#eef2f7]">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: `${(c.cents / model.maxClient) * 100}%`,
                          background:
                            i === 0
                              ? '#2563eb'
                              : i === 1
                                ? '#2563eb'
                                : i === 2
                                  ? '#3b82f6'
                                  : i === 3
                                    ? '#60a5fa'
                                    : '#93c5fd',
                        }}
                      />
                    </div>
                  </div>
                ))}
                {model.topClients.length === 0 && (
                  <p className="py-4 text-center text-sm text-gray-400">
                    No issued invoices in this window.
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SplitRow({
  color,
  label,
  cents,
  pct,
}: {
  color: string;
  label: string;
  cents: number;
  pct: number;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-2 text-[13px] text-gray-700">
        <span
          className="h-2.5 w-2.5 rounded-[3px]"
          style={{ background: color }}
        />
        {label}
      </span>
      <span className="font-mono text-[13px] font-semibold text-gray-900">
        {formatMoney(centsToMoney(cents))} · {Math.round(pct)}%
      </span>
    </div>
  );
}
